import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import {
  FiArrowRight,
  FiCalendar,
  FiMapPin,
  FiMusic,
  FiPhone,
  FiStar,
} from "react-icons/fi";
import AnimatedContainer from "../../container/animatedContainer";
import { useGetEventsQuery } from "../../api/event";
import { campaign, isCampaignEvent } from "../../config/campaign";
import { isEventSalesClosed, sortEventPricings } from "../../utils";

export function HomePage() {
  const { data, isLoading } = useGetEventsQuery({
    pageSize: campaign.stops.length,
    search: campaign.title,
  });
  const concertStop = campaign.stops[0];
  const campaignEvent =
    data?.list.find((item) => item.slug === concertStop.slug) ??
    data?.list.find((item) => isCampaignEvent(item.title, item.slug));
  const eventLink = `/event/${campaignEvent?.slug ?? concertStop.slug}`;
  const isSalesClosed =
    isEventSalesClosed(campaignEvent) ||
    isEventSalesClosed({ endDate: concertStop.isoDate, status: "active" });
  const ticketTiers = campaignEvent?.pricings
    ? sortEventPricings(campaignEvent.pricings).map((item) => {
        const gate = campaign.fallbackTickets.find(
          (fallback) => fallback.type === item.type.toUpperCase()
        );
        return {
          type: item.type,
          price: item.price,
          gatePrice:
            gate && gate.price > item.price ? gate.price : undefined,
        };
      })
    : campaign.fallbackTickets.map((item) => ({
        type: item.type,
        price: item.preSellPrice ?? item.price,
        gatePrice: item.preSellPrice ? item.price : undefined,
      }));

  return (
    <AnimatedContainer>
      <div className="min-h-screen bg-[#06130a] text-white">
        <section className="relative min-h-[74svh] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${campaign.poster}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#06130a] via-[#08170d]/85 to-[#1c5228]/35" />
          <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#06130a] to-transparent" />

          <div className="relative mx-auto flex min-h-[74svh] w-full max-w-[1500px] flex-col justify-end px-5 pb-12 pt-16 md:px-12 lg:px-16">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.28em] text-[#e8b64c]">
                {campaign.organiser} in partnership with {campaign.poweredBy}
              </p>
              <h1 className="mt-4 max-w-2xl text-5xl font-black uppercase leading-[0.92] text-[#f6d98a] sm:text-7xl lg:text-8xl">
                {campaign.title}
              </h1>
              <p className="mt-4 text-3xl font-black text-white sm:text-5xl">
                {campaign.season}
              </p>
              <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-white/80 sm:text-lg">
                A night of worship with {campaign.organiser}, live at{" "}
                {campaign.venue} on Sunday {concertStop.date}.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to={eventLink}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-[#9d1c1c] px-6 py-4 text-sm font-black uppercase tracking-[0.12em] text-white shadow-[0_18px_45px_rgba(157,28,28,0.32)] transition hover:bg-[#b52a2a]"
                >
                  Buy tickets
                  <FiArrowRight className="text-lg" />
                </Link>
                <a
                  href="#tickets"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-white/25 bg-white/10 px-6 py-4 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:bg-white/15"
                >
                  View tickets
                  <FiStar className="text-lg" />
                </a>
              </div>
            </div>

            <div className="mt-10 grid w-full max-w-4xl grid-cols-1 gap-3 sm:grid-cols-3">
              <HeroStat
                icon={<FiCalendar />}
                label="Date"
                value={`Sun. ${concertStop.shortDate} ${campaign.year}`}
              />
              <HeroStat
                icon={<FiMapPin />}
                label="Venue"
                value={campaign.venue}
              />
              <HeroStat
                icon={<FiStar />}
                label="Ticket tiers"
                value={ticketTiers.length.toString()}
              />
            </div>
          </div>
        </section>

        <section
          id="tickets"
          className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-5 px-5 py-10 md:px-12 lg:grid-cols-[0.95fr_1.05fr] lg:px-16"
        >
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#e8b64c]">
              Tickets available now
            </p>
            <h2 className="mt-2 max-w-2xl text-3xl font-black uppercase text-white sm:text-5xl">
              Choose your ticket tier.
            </h2>
            <p className="mt-4 max-w-xl text-white/70">
              Pre-sell prices are available for a limited time — secure your
              seat early and pay less than at the gate.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {isLoading
              ? [1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="h-40 rounded-lg bg-white/10 animate-pulse"
                  />
                ))
              : ticketTiers.map((tier, index) => (
                  <Link
                    to={eventLink}
                    key={tier.type}
                    className={`group rounded-lg border border-white/10 bg-white/[0.06] p-4 transition hover:-translate-y-1 hover:border-[#e8b64c]/60 hover:bg-white/[0.09] ${
                      isSalesClosed ? "opacity-65" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#9d1c1c] text-xl text-white">
                        <FiMusic />
                      </div>
                      <span className="text-xs font-black uppercase tracking-[0.2em] text-white/35">
                        {isSalesClosed ? "Done" : `0${index + 1}`}
                      </span>
                    </div>
                    <h3 className="mt-5 text-2xl font-black uppercase text-[#f6d98a]">
                      {tier.type}
                    </h3>
                    <p className="mt-2 text-2xl font-black text-white">
                      {tier.price.toLocaleString()} RWF
                    </p>
                    {tier.gatePrice ? (
                      <p className="mt-1 text-sm font-bold uppercase tracking-[0.12em] text-[#e8b64c]">
                        <span className="text-white/45 line-through">
                          {tier.gatePrice.toLocaleString()} RWF
                        </span>{" "}
                        Pre-sell price
                      </p>
                    ) : null}
                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-black uppercase tracking-[0.12em] text-[#e8b64c]">
                      {isSalesClosed ? "View event" : "Get ticket"}
                      <FiArrowRight className="transition group-hover:translate-x-1" />
                    </div>
                  </Link>
                ))}
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-5 px-5 pb-14 md:px-12 lg:grid-cols-[1.1fr_0.9fr] lg:px-16">
          <div className="rounded-lg border border-white/10 bg-[#0b1d10] p-5 md:p-7">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#e8b64c]">
                  Live at {campaign.venue}
                </p>
                <h2 className="mt-2 text-3xl font-black uppercase text-white sm:text-4xl">
                  Sunday {concertStop.date}.
                </h2>
              </div>
              <Link
                to={eventLink}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-[#06130a] transition hover:bg-[#f6d98a]"
              >
                Checkout
                <FiArrowRight />
              </Link>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
              <InfoCard
                icon={<FiCalendar />}
                label="Date"
                value={`Sunday, ${concertStop.date}`}
              />
              <InfoCard
                icon={<FiMapPin />}
                label="Venue"
                value={`${campaign.venue}, Rwanda`}
              />
              <InfoCard
                icon={<FiPhone />}
                label="More info"
                value={campaign.contactPhone}
              />
            </div>
          </div>

          <div className="min-h-72 overflow-hidden rounded-lg border border-white/10 bg-white/[0.06]">
            <div
              className="h-full min-h-72 bg-cover bg-center"
              style={{ backgroundImage: `url('${campaign.poster}')` }}
              aria-label={`${campaign.title} poster`}
            />
          </div>
        </section>
      </div>
    </AnimatedContainer>
  );
}

function HeroStat({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10 text-xl text-[#e8b64c]">
        {icon}
      </div>
      <div>
        <p className="text-xl font-black text-white">{value}</p>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">
          {label}
        </p>
      </div>
    </div>
  );
}

function InfoCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.06] p-5">
      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#e8b64c] text-xl text-[#06130a]">
        {icon}
      </div>
      <p className="mt-5 text-sm font-black uppercase tracking-[0.18em] text-white/55">
        {label}
      </p>
      <p className="mt-2 text-lg font-black text-white">{value}</p>
    </div>
  );
}
